import React from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import { FormAccordion, FormAccordionItem } from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Grid } from '@mantine/core';
import {
  getFinancialFormSetupValues,
  ProductFormComponentProps,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { FormTitle } from 'src/financial/financial-data/financial-item/forms/common/FormTitle';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { DynamicUiProviderWrapper } from 'src/financial/financial-data/financial-item/forms/common/DynamicUiProviderWrapper';
import { FinancialFormField } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormField';
import { GridPair } from 'src/financial/financial-data/financial-item/forms/common/GridPair';
import {
  BENEFICIARY,
  BeneficiaryFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/BeneficiaryFormAccordionItemList';
import styles from 'src/financial/financial-data/financial-item/forms/common/FormLayout.module.css';
import { FeeFormAccordionItemList } from 'src/financial/financial-data/financial-item/forms/common/FeeFormAccordionItemList';
import {
  FeedInfo,
  FinancialFormContainer,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';

const PROTECTION_PRODUCT = 'Protection';
const FEE = 'ProtectionFee';

export function Protection(props: ProductFormComponentProps) {
  const { onSave } = props;

  const { isCreateMode, uiData, initialValues, onDeleteConfirm, onConnectAsset, feedInfo } =
    getFinancialFormSetupValues(PROTECTION_PRODUCT, props, [
      { product: BENEFICIARY, field: 'beneficiaries' },
      { product: FEE, field: 'fees' },
    ]);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues,
  });

  const handleSave = async (values: FormValuesType) => {
    if (
      BENEFICIARY in values &&
      Array.isArray(values[BENEFICIARY]) &&
      FEE in values &&
      Array.isArray(values[FEE])
    ) {
      const mappedValues = {
        ...values[PROTECTION_PRODUCT],
        beneficiaries: values[BENEFICIARY].map(item => ({ ...item })),
        fees: values[FEE].map(item => ({ ...item })),
      };

      await onSave(mappedValues, uiData.ui.entitiesMetadata[PROTECTION_PRODUCT]);
    }
  };

  return (
    <DynamicUiProviderWrapper
      form={form}
      uiData={uiData}
      onDeleteConfirm={isCreateMode ? () => {} : onDeleteConfirm}
      isCreateMode={isCreateMode}
    >
      <form onSubmit={form.onSubmit(handleSave)} autoComplete="off">
        <FormContent form={form} feedInfo={feedInfo} onConnect={onConnectAsset} />
      </form>
    </DynamicUiProviderWrapper>
  );
}

type FormContentProps = {
  form: UseFormReturnType<FormValuesType>;
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
};

const FormContent = ({ form, feedInfo, onConnect }: FormContentProps) => {
  const { isEditing } = useDynamicUI();

  return (
    <FinancialFormContainer
      title={<FormTitle title="Protection" />}
      feedInfo={feedInfo}
      onConnect={onConnect}
    >
      <FormAccordion defaultValue={['summary', 'cover', 'term', 'premium', 'beneficiaries', 'fee']}>
        <FormAccordionItem value="summary" label="Summary">
          <Grid>
            <GridPair
              first={<FinancialFormField field="type" product={PROTECTION_PRODUCT} />}
              second={<FinancialFormField field="description" product={PROTECTION_PRODUCT} />}
            />
            <GridPair
              isHighlighted
              first={<FinancialFormField field="provider" product={PROTECTION_PRODUCT} />}
              second={
                <FinancialFormField
                  field="provider_reference_number"
                  product={PROTECTION_PRODUCT}
                />
              }
            />
            <GridPair
              first={<FinancialFormField field="status" product={PROTECTION_PRODUCT} />}
              second={<FinancialFormField field="is_joint" product={PROTECTION_PRODUCT} />}
            />
            <GridPair
              first={
                <FinancialFormField field="is_serviced_by_advisor" product={PROTECTION_PRODUCT} />
              }
            />
          </Grid>
        </FormAccordionItem>
        <FormAccordionItem value="cover" label="Cover amount and information">
          <Grid>
            <GridPair
              first={<FinancialFormField field="cover_amount" product={PROTECTION_PRODUCT} />}
              second={<FinancialFormField field="benefit_type" product={PROTECTION_PRODUCT} />}
            />
            <GridPair
              isHighlighted
              first={
                <FinancialFormField field="benefit_payment_type" product={PROTECTION_PRODUCT} />
              }
              second={
                <FinancialFormField
                  field="benefit_payment_period_in_months"
                  product={PROTECTION_PRODUCT}
                />
              }
            />
            <GridPair
              first={<FinancialFormField field="protection_need" product={PROTECTION_PRODUCT} />}
              second={<FinancialFormField field="is_in_trust" product={PROTECTION_PRODUCT} />}
            />
            <GridPair
              first={<FinancialFormField field="life_assured" product={PROTECTION_PRODUCT} />}
            />
          </Grid>
        </FormAccordionItem>
        <FormAccordionItem value="term" label="Term">
          <Grid className={styles.highlightedGrid}>
            <GridPair
              first={<FinancialFormField field="term_start_date" product={PROTECTION_PRODUCT} />}
              second={<FinancialFormField field="term_end_date" product={PROTECTION_PRODUCT} />}
            />
            <GridPair
              first={<FinancialFormField field="term_in_months" product={PROTECTION_PRODUCT} />}
            />
          </Grid>
        </FormAccordionItem>
        <FormAccordionItem value="premium" label="Premium summary">
          <Grid>
            <GridPair
              isHighlighted
              first={<FinancialFormField field="premium_amount" product={PROTECTION_PRODUCT} />}
              second={<FinancialFormField field="premium_type" product={PROTECTION_PRODUCT} />}
            />
            <GridPair
              first={<FinancialFormField field="premium_frequency" product={PROTECTION_PRODUCT} />}
              second={
                <FinancialFormField field="has_waiver_of_premium" product={PROTECTION_PRODUCT} />
              }
            />
          </Grid>
        </FormAccordionItem>
        <FeeFormAccordionItemList form={form} isEditing={isEditing} feeType={FEE} />
        <BeneficiaryFormAccordionItemList form={form} isEditing={isEditing} />
      </FormAccordion>
    </FinancialFormContainer>
  );
};
